/**
 * Token-aware parsers for window-function syntax: per-item OVER detection
 * (extractWindowFunction) plus the standalone WINDOW-clause body splitter
 * (extractNamedWindows). v1 keeps OVER bodies opaque per § 1.4.
 */
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { SkipContext } from '../../../../sql-resolver/classify/tokenSkip';
import { skipContextFor, trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';
import { isWordBoundary } from './extractSetOperatorBranches';

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_$]/;

export interface WindowItem {
  readonly funcText: string;
  readonly overBody: string | null;
  readonly namedWindow: string | null;
  readonly alias: string | null;
}

export interface NamedWindowDef {
  readonly name: string;
  readonly bodyText: string;
}

export function detectOverInItem(source: string, start: number, end: number, options: ParseOptions): boolean {
  return findOverPosition(source, start, end, skipContextFor(options)) >= 0;
}

export function extractWindowFunction(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): WindowItem | null {
  const ctx = skipContextFor(options);
  const overStart = findOverPosition(source, start, end, ctx);
  if (overStart < 0) return null;
  const funcText = source.slice(start, overStart).trim();

  let cursor = overStart + 'OVER'.length;
  while (cursor < end && /\s/.test(source[cursor])) cursor++;

  let overBody: string | null = null;
  let namedWindow: string | null = null;
  let afterSpec = cursor;

  if (cursor < end && source[cursor] === '(') {
    const groupEnd = consumeParenGroup(source, cursor, end, ctx);
    overBody = source.slice(cursor + 1, Math.max(cursor + 1, groupEnd - 1)).trim();
    afterSpec = groupEnd;
  } else if (cursor < end && IDENT_START.test(source[cursor])) {
    const ws = cursor;
    cursor++;
    while (cursor < end && IDENT_CONT.test(source[cursor])) cursor++;
    namedWindow = stripQuotes(source.slice(ws, cursor));
    afterSpec = cursor;
  }

  return { funcText, overBody, namedWindow, alias: extractTrailingAlias(source, afterSpec, end, ctx) };
}

// KNOWN GAP (pre-existing, out of scope for §4b-ii-fix): this scanner's
// IDENT_START-gated identifier read never recognizes a QUOTED window name
// (`"My Window"`, `` `w` ``, `[w]`) — the opening quote char fails
// IDENT_START.test and is skipped one byte at a time as if it were ordinary
// punctuation, so anything beyond a single-word quoted body desyncs the rest
// of the scan (the closing quote then fails the `AS` / `(` lookahead and the
// definition is dropped). Only bare (unquoted) window names round-trip
// correctly. Bare names are the overwhelming common case and are what the
// resolver-side rename coordination (`applyStructuralEdit.namedWindow.ts`)
// is proven against.
export function extractNamedWindows(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): NamedWindowDef[] {
  const ctx = skipContextFor(options);
  const out: NamedWindowDef[] = [];
  let i = start;
  while (i < end) {
    while (i < end && (/\s/.test(source[i]) || source[i] === ',')) i++;
    if (i >= end || !IDENT_START.test(source[i])) { i++; continue; }
    const ns = i;
    i++;
    while (i < end && IDENT_CONT.test(source[i])) i++;
    const nameEnd = i;
    const name = stripQuotes(source.slice(ns, nameEnd));
    while (i < end && /\s/.test(source[i])) i++;
    if (i + 2 <= end && source.slice(i, i + 2).toUpperCase() === 'AS') {
      i += 2;
      while (i < end && /\s/.test(source[i])) i++;
    }
    if (i >= end || source[i] !== '(') break;
    const openParen = i;
    const groupEnd = consumeParenGroup(source, i, end, ctx);
    const bodyStart = openParen + 1;
    // groupEnd is one-past the close paren; the inner body ends at the paren.
    const bodyEnd = groupEnd > openParen + 1 && source[groupEnd - 1] === ')' ? groupEnd - 1 : groupEnd;
    out.push({
      name,
      bodyText: source.slice(bodyStart, Math.max(bodyStart, bodyEnd)).trim(),
    });
    i = groupEnd;
  }
  return out;
}

function findOverPosition(source: string, start: number, end: number, ctx: SkipContext): number {
  let i = start;
  let paren = 0;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped > i ? skipped : i + 1; continue; }
    const c = source[i];
    if (c === '(') { paren++; i++; continue; }
    if (c === ')') { if (paren > 0) paren--; i++; continue; }
    if (paren === 0 && IDENT_START.test(c)) {
      const ws = i;
      i++;
      while (i < end && IDENT_CONT.test(source[i])) i++;
      if (source.slice(ws, i).toUpperCase() === 'OVER' && isWordBoundary(source, ws - 1) && isWordBoundary(source, i)) {
        return ws;
      }
      continue;
    }
    i++;
  }
  return -1;
}

function consumeParenGroup(source: string, start: number, end: number, ctx: SkipContext): number {
  let depth = 0;
  let i = start;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped > i ? skipped : i + 1; continue; }
    if (source[i] === '(') { depth++; i++; continue; }
    if (source[i] === ')') { depth--; i++; if (depth === 0) return i; continue; }
    i++;
  }
  return i;
}

function extractTrailingAlias(source: string, start: number, end: number, ctx: SkipContext): string | null {
  let i = start;
  while (i < end && /\s/.test(source[i])) i++;
  if (i >= end) return null;
  const skipped = trySkipNonCode(source, i, end, ctx);
  if (skipped >= 0 && skipped > i) {
    i = skipped;
    while (i < end && /\s/.test(source[i])) i++;
  }
  if (i >= end) return null;
  const remaining = source.slice(i, end).trim();
  if (remaining.length === 0) return null;
  const upper = remaining.toUpperCase();
  if (upper.startsWith('AS') && (remaining.length === 2 || /\s/.test(remaining[2]))) {
    return stripQuotes(remaining.slice(2).trim());
  }
  if (IDENT_START.test(remaining[0]) || remaining[0] === '"' || remaining[0] === '`' || remaining[0] === '[') {
    return stripQuotes(remaining);
  }
  return null;
}

function stripQuotes(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.length >= 2) {
    const first = trimmed[0];
    const last = trimmed[trimmed.length - 1];
    if ((first === '"' && last === '"') || (first === '`' && last === '`')) {
      return trimmed.slice(1, -1).replace(/""/g, '"');
    }
    if (first === '[' && last === ']') return trimmed.slice(1, -1);
  }
  return trimmed;
}
